/** XeAgents.ai**/
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertTriangle, ArrowRight, CornerDownRight, Download, Eye, Flag, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { buildFlowScript, type ScriptStep } from "@shared/flow-script";

/**
 * The whole flow, read top to bottom as a script.
 *
 * The canvas answers "how is this wired"; it does not answer "what does this
 * agent actually say", because that is spread across fifty nodes nobody opens
 * one at a time. That gap is not cosmetic - it is where a prompt telling an
 * agent to narrate its own routing sat until it reached a live call.
 *
 * So: every spoken line, in call order, with each branch named in words and
 * pointing at the step it leads to. Read-only on purpose. Most nodes carry more
 * than words - conditions, variables, tool wiring - so a script can describe a
 * flow but cannot rebuild one, and an editable script would quietly drop
 * whatever it does not show. Editing stays on the canvas, and every step here
 * clicks through to its node.
 *
 * Built from the nodes and edges currently on screen rather than from what is
 * saved, so it reflects unsaved edits - the point is to read the flow you are
 * about to ship, not the one you shipped last time.
 */
export function FlowScriptPanel({
  open,
  onOpenChange,
  flowName,
  nodes,
  edges,
  onSelectNode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowName?: string;
  nodes: any[];
  edges: any[];
  onSelectNode?: (nodeId: string) => void;
}) {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const script = useMemo(() => buildFlowScript(nodes, edges), [nodes, edges]);

  /**
   * Download the script as a PDF.
   *
   * Posts the canvas's own nodes and edges rather than a flow id, so the
   * document matches what is on screen - unsaved edits included. The server
   * renders it from the same buildFlowScript this panel uses, so the PDF and
   * the page cannot disagree about call order or branch wording.
   */
  const exportPdf = async () => {
    setExporting(true);
    try {
      // apiRequest throws on a non-2xx, so anything past here has a body.
      const response = await apiRequest("POST", "/api/flow-automation/flows/script-pdf", {
        name: flowName,
        nodes,
        edges,
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(flowName || "flow").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-") || "flow"}-script.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoked on the next tick: Safari has not started reading the blob yet
      // when click() returns, and a revoked URL downloads an empty file.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error: any) {
      toast({
        title: "Could not export the script",
        description: error?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-2xl"
        data-testid="flow-script-panel"
      >
        <SheetHeader className="space-y-2">
          <SheetTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Call Script
          </SheetTitle>
          <SheetDescription>
            Everything this agent can say, in the order a call reaches it. Read-only —
            select a step to edit it on the canvas.
          </SheetDescription>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={exportPdf}
            disabled={exporting || script.steps.length === 0}
            data-testid="button-export-script-pdf"
          >
            {exporting ? (
              <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Building PDF…</>
            ) : (
              <><Download className="mr-2 h-3.5 w-3.5" />Export as PDF</>
            )}
          </Button>
        </SheetHeader>

        <ScriptSummary script={script} />

        <ol className="mt-4 space-y-3 pb-10">
          {script.steps.map((step) => (
            <StepCard key={step.nodeId} step={step} onSelectNode={onSelectNode} />
          ))}
        </ol>

        {script.steps.length === 0 && (
          <p className="mt-6 text-sm text-muted-foreground" data-testid="flow-script-empty">
            This flow has no steps yet. Add nodes on the canvas and they will appear here.
          </p>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** The shape of the flow in four numbers, plus anything that looks wrong. */
function ScriptSummary({ script }: { script: ReturnType<typeof buildFlowScript> }) {
  return (
    <div className="mt-4 space-y-3 rounded-lg border bg-muted/30 p-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span><strong className="text-foreground">{script.steps.length}</strong> steps</span>
        <span><strong className="text-foreground">{script.totalLines}</strong> spoken lines</span>
        <span><strong className="text-foreground">{script.variables.length}</strong> variables</span>
        <span><strong className="text-foreground">{script.outcomes.length}</strong> outcomes</span>
      </div>

      {script.variables.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="mr-1 text-xs text-muted-foreground">Fills in:</span>
          {script.variables.map((name) => (
            <Badge key={name} variant="secondary" className="h-5 px-1.5 font-mono text-[10px]">
              {name}
            </Badge>
          ))}
        </div>
      )}

      {/* A step nothing routes to can never run. Worth saying out loud here,
          because on a canvas it looks identical to every other node. */}
      {script.orphans.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          {script.orphans.length} step{script.orphans.length === 1 ? "" : "s"} can never be
          reached — nothing routes to {script.orphans.length === 1 ? "it" : "them"}.
        </p>
      )}
    </div>
  );
}

function StepCard({
  step,
  onSelectNode,
}: {
  step: ScriptStep;
  onSelectNode?: (nodeId: string) => void;
}) {
  const silent = step.says.length === 0;

  return (
    <li
      className={`rounded-lg border p-3 ${step.unreachable ? "border-amber-300 bg-amber-50/50 dark:border-amber-900/50 dark:bg-amber-950/20" : "bg-card"}`}
      data-testid={`flow-script-step-${step.nodeId}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <span className="text-muted-foreground">{step.step}.</span>
            <span className="truncate">{step.label}</span>
            <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal">
              {step.kind}
            </Badge>
            {step.unreachable && (
              <Badge variant="outline" className="h-5 border-amber-400 px-1.5 text-[10px] font-normal text-amber-700 dark:text-amber-400">
                unreachable
              </Badge>
            )}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">{step.summary}</p>
        </div>
        {onSelectNode && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 px-2 text-xs font-normal text-muted-foreground hover:text-foreground"
            onClick={() => onSelectNode(step.nodeId)}
            data-testid={`button-goto-${step.nodeId}`}
          >
            Edit
          </Button>
        )}
      </div>

      {step.says.length > 0 && (
        <div className="mt-2.5 space-y-1.5">
          {step.says.map((line, index) => (
            <p key={index} className="text-sm leading-relaxed">
              <span className="mr-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                {line.role}
              </span>
              <span className="text-foreground/90">“{line.text}”</span>
            </p>
          ))}
        </div>
      )}

      {/* Routers say nothing at all. Without this they read as a step the agent
          skipped, rather than the branch point they are. The start node is also
          silent, but it is not listening - it is where the call begins. */}
      {silent && step.branches.length > 0 && step.kind !== 'start' && (
        <p className="mt-2 text-xs italic text-muted-foreground">
          Says nothing — listens, then picks a branch.
        </p>
      )}

      {step.collects.length > 0 && (
        <p className="mt-2 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          Records:
          {step.collects.map((name) => (
            <Badge key={name} variant="secondary" className="h-5 px-1.5 font-mono text-[10px]">
              {name}
            </Badge>
          ))}
        </p>
      )}

      {step.branches.length > 0 && (
        <ul className="mt-2.5 space-y-1 border-t pt-2">
          {step.branches.map((branch, index) => (
            <li key={index} className="flex items-start gap-1.5 text-xs">
              <CornerDownRight className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="min-w-0">
                <span className="text-foreground/80">{branch.when}</span>
                <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" />
                <button
                  type="button"
                  className="text-primary hover:underline disabled:cursor-default disabled:text-muted-foreground disabled:no-underline"
                  disabled={!onSelectNode || branch.toStep === undefined}
                  onClick={() => onSelectNode?.(branch.toNodeId)}
                >
                  {branch.toStep !== undefined ? `Step ${branch.toStep}` : "missing step"} · {branch.toLabel}
                </button>
                {branch.examples.length > 0 && (
                  <span className="ml-1 text-muted-foreground">
                    e.g. “{branch.examples[0]}”
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {step.outcome && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Flag className="h-3 w-3" />
          Call recorded as <span className="font-mono text-[11px] text-foreground/80">{step.outcome}</span>
        </p>
      )}
    </li>
  );
}
